import React, { useCallback, useMemo, useRef, useState } from "react";
import toast from "react-hot-toast";

import EnterYourPostcode from "../components/postcode/EnterYourPostcode";
import Price from "../components/price/Price";
import SelectArrivalRange from "../components/range/SelectArrivalRange";
import SelectSkills from "../components/skills/SelectSkills";
import { createJob, findFtmInRetryJob } from "../service";
import useCustomNavigate from "../../../common/hooks/useCustomNavigate";
import { getSettings, setBackupJobDetails } from "../../../storage";
import StepFlow from "./StepFlow";
import JobNote from "../components/jobNote/JobNote";

const PageBody = ({
    address,
    setAddress,
    jobGeolocationDetails,
    setJobGeolocationDetails,
    range,
    defaultRange,
    setRange,
    skill,
    setSkill,
    subSkill,
    setSubSkill,
    calculateFutureTime,
    jobRatePreHour,
    handleReset,
    confirmBooking,
    showAddressSearch,
    isHome = false,
    retryJob,
    step, 
    setStep,
    jobNotes,
    setJobNotes,
    isCheckingPrice,
    bookAgain
}) => {
    const formEditAddressRef = useRef(null);
    const { navigateToCustom } = useCustomNavigate();

    const [addressStepSuccessCallback, setAddressStepSuccessCallback] = useState(null);

    const settings = getSettings();
    const stepFlowIsEnable = settings.customer_web_job_step_flow_is_enable;

    const runRetryJob = useCallback(async () => {
        try {
            const response = await findFtmInRetryJob({
                job_id: retryJob,
                new_eta_start_time: calculateFutureTime(range[0]),
                new_eta_end_time: calculateFutureTime(range[1]),
            });
            if (response?.status === "Success") {
                navigateToCustom(`/current/job/${retryJob}`);
            }
        } catch (error) {
            console.error("Error retrying job:", error);
        }
    }, [retryJob, calculateFutureTime, range, navigateToCustom]);

    const sections = useMemo(
        () => [
            {
                key: "skills",
                component: (
                    <SelectSkills
                        skill={skill}
                        setSkill={setSkill}
                        subSkill={subSkill}
                        setSubSkill={setSubSkill}
                        isHome={true}
                    />
                ),
            },
            {
                key: "notes",
                component: (
                    <JobNote
                        subSkill={subSkill}
                        jobNotes={jobNotes}
                        setJobNotes={setJobNotes}
                        isHome={isHome}
                    />
                ),
            },
            {
                key: "postcode",
                component: (
                    <EnterYourPostcode
                        address={address}
                        setAddress={setAddress}
                        jobGeolocationDetails={jobGeolocationDetails}
                        setJobGeolocationDetails={setJobGeolocationDetails}
                        handleReset={handleReset}
                        isHome={true}
                        isCheckingPrice={isCheckingPrice}
                        showAddressSearch={showAddressSearch}
                        retryJob={retryJob}
                        stepFlowIsEnable={stepFlowIsEnable}
                        formEditAddressRef={formEditAddressRef}
                        onStepSuccess={addressStepSuccessCallback} 
                    />
                ),
            },
            {
                key: "range",
                component: (
                    <SelectArrivalRange
                        range={range}
                        defaultRange={defaultRange}
                        setRange={setRange}
                        calculateFutureTime={calculateFutureTime}
                        skill={skill}
                        subSkill={subSkill}
                        isHome={true}
                        jobRatePreHour={jobRatePreHour}
                        retryJob={retryJob}
                        stepFlowIsEnable={stepFlowIsEnable}
                    />
                ),
            },
            {
                key: "price",
                component: (
                    <Price
                        jobRatePreHour={jobRatePreHour}
                        confirmBooking={confirmBooking}
                        isHome={true}
                        skill={skill}
                        calculateFutureTime={calculateFutureTime}
                        range={range}
                    />
                ),
            },
        ],
        [
            skill, setSkill, subSkill, setSubSkill, jobNotes, setJobNotes, isHome, isCheckingPrice,
            address, setAddress, jobGeolocationDetails, setJobGeolocationDetails, 
            handleReset, showAddressSearch, retryJob, stepFlowIsEnable, addressStepSuccessCallback, 
            range, defaultRange, setRange, calculateFutureTime, jobRatePreHour, confirmBooking
        ]
    );

    const jobCreate = async ({ creditNoteCode, description }) => {
        try {
            const eta_start_time = calculateFutureTime(range[0]);
            const eta_end_time = calculateFutureTime(range[1]);

            setBackupJobDetails({
                address,
                jobGeolocationDetails,
                range,
                skill,
                subSkill,
                jobRatePreHour,
                jobNotes
            });

            const response = await createJob({
                job_description: description,
                job_skill_id: subSkill?.sub_skill_id,
                job_latitude: jobGeolocationDetails?.location?.lat,
                job_longitude: jobGeolocationDetails?.location?.lng,
                job_address: jobGeolocationDetails?.address,
                job_postcode: jobGeolocationDetails?.postcode,
                job_eta_end_time: eta_start_time,
                job_eta_start_time: eta_end_time,
                job_note: jobNotes,
                cn_code: creditNoteCode,
            });

            return response;
        } catch (error) {
            toast.error("An error occurred while creating the job.");
        }
    };

    return (
        <StepFlow
            step={step}
            setStep={setStep}
            sections={sections}
            skill={skill}
            jobRatePreHour={jobRatePreHour}
            calculateFutureTime={calculateFutureTime}
            range={range}
            subSkill={subSkill}
            jobCreate={jobCreate}
            runRetryJob={runRetryJob}
            formEditAddressRef={formEditAddressRef}
            setAddressStepSuccessCallback={setAddressStepSuccessCallback}
            jobGeolocationDetails={jobGeolocationDetails}
            retryJob={retryJob}
            isHome={isHome}
            confirmBooking={confirmBooking}
            bookAgain={bookAgain}
        />
    );
};

export default PageBody;